import { OAuth2 } from '@backstage/core-app-api';
import { ProxiedSignInPage, SignInPage } from '@backstage/core-components';
import {
  type BackstageIdentityApi,
  type OAuthApi,
  type OpenIdConnectApi,
  type ProfileInfoApi,
  type SessionApi,
  configApiRef,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import {
  SignInPageBlueprint,
  type SignInPageProps,
} from '@backstage/plugin-app-react';

export const enterpriseOidcAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({
  id: 'horizon.auth.enterprise-oidc',
});

const enterpriseOidcApi = ApiBlueprint.make({
  name: 'horizon-enterprise-oidc',
  params: defineParams =>
    defineParams({
      api: enterpriseOidcAuthApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
      },
      factory: ({ configApi, discoveryApi, oauthRequestApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getString('auth.environment'),
          defaultScopes: ['openid', 'profile', 'email'],
          provider: {
            id: 'oidc',
            title: 'Enterprise SSO',
            icon: LockOutlinedIcon,
            message: 'Sign in with your organization-managed identity.',
          },
        }),
    }),
});

function HorizonSignInPage(props: SignInPageProps) {
  const configApi = useApi(configApiRef);
  const environment = configApi.getOptionalString('auth.environment');

  if (environment === 'development') {
    // Unlike the stock guest button, the proxied page has no legacy,
    // tokenless fallback. A missing server-controlled Catalog mapping is
    // therefore shown as a sign-in failure instead of a misleading session.
    return <ProxiedSignInPage {...props} provider="guest" />;
  }

  return (
    <SignInPage
      {...props}
      provider={{
        id: 'oidc',
        title: 'Enterprise SSO',
        message: 'Sign in with your organization-managed identity.',
        apiRef: enterpriseOidcAuthApiRef,
      }}
    />
  );
}

const horizonSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => HorizonSignInPage,
  },
});

export const horizonAuthModule = createFrontendModule({
  pluginId: 'app',
  extensions: [enterpriseOidcApi, horizonSignInPage],
});
